<template>
  <div class="animation-canvas-wrapper" @mouseenter="showControls = true" @mouseleave="showControls = false">
    <canvas ref="canvas" :width="width" :height="height" class="animation-canvas" />
    <transition name="fade">
      <div v-show="showControls" class="controls-overlay">
        <button class="icon-btn" @click="play" :disabled="playing" :title="'播放'">
          <i class="el-icon-video-play" v-if="!playing"></i>
        </button>

        <button class="icon-btn" @click="reload" :title="'重新加载'">
          <i class="el-icon-refresh"></i>
        </button>
      </div>
    </transition>
    <transition name="fade">
      <div v-show="showControls" class="progress-overlay">
        <span class="frame-info">
          <template v-if="flow">
            <span v-if="currentNode">当前节点：{{ currentNode.name || currentNode.id }}</span>
            <span v-else>未开始</span>
          </template>
          <template v-else>
            {{ currentFrame + 1 }} / {{ totalFrames }}
          </template>
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import AnimationPlayer from '../utils/animation_player.js'

export default {
  name: 'AnimationCanvas',
  props: {
    flow: Object,
    url: String,
    width: { type: Number, default: 320 },
    height: { type: Number, default: 240 }
  },
  data () {
    return {
      player: null,
      playing: false,
      currentFrame: 0,
      totalFrames: 0,
      sliderFrame: 0,
      ignoreSliderWatch: false,
      showControls: true,
      currentNode: null // flow模式下当前节点
    }
  },
  watch: {
    flow (newFlow) {
      if (!newFlow) return
      this.initPlayer()
      this.player.loadFromFlow(newFlow, {
        onStatusChange: status => this.$emit('status-change', status),
        onNodeChange: node => {
          this.currentNode = node
          this.$emit('node-change', node)
        },
        onAnimationEnded: () => {
          this.playing = false
          this.$emit('ended')
        }
      })
      this.playFlow()
      this.updateTotalFrames()
    },
    url (newUrl) {
      if (!newUrl) return
      this.initPlayer()
      this.player.loadFromUrl(newUrl).then(() => {
        this.updateTotalFrames()
        // 不自动播放
        // this.play()
      })
    },
    currentFrame (val) {
      if (!this.ignoreSliderWatch) this.sliderFrame = val
    }
  },
  mounted () {
    this.initPlayer()
    if (this.flow) {
      this.player.loadFromFlow(this.flow, {
        onStatusChange: status => this.$emit('status-change', status),
        onNodeChange: node => {
          this.currentNode = node
          this.$emit('node-change', node)
        },
        onAnimationEnded: () => {
          this.playing = false
          this.$emit('ended')
        }
      })
      this.playFlow()
    } else if (this.url) {
      this.player.loadFromUrl(this.url).then(() => {
        this.updateTotalFrames()
        // 不自动播放
        // this.play()
      })
    }
    this.updateTotalFrames()
  },
  beforeDestroy () {
    if (this.player) this.player.destroy()
  },
  methods: {
    initPlayer () {
      if (this.player) {
        this.player.destroy()
        this.player = null
      }
      this.player = new AnimationPlayer({
        canvas: this.$refs.canvas,
        width: this.width,
        height: this.height
      })
      this.player.onFrameChanged(idx => {
        this.currentFrame = idx
        if (!this.ignoreSliderWatch) this.sliderFrame = idx
        this.$emit('frame-change', idx)
      })
      // 如果不是流程模式，设置默认的结束回调
      if (!this.flow) {
        this.player.onEnded(() => {
          this.playing = false
          this.stop() // 播放完毕后设置为停止状态
          this.$emit('ended')
        })
      }
    },
    play () {
      if (this.player) {
        this.player.play()
        this.playing = true
      }
    },
    stop () {
      if (this.player) {
        this.player.stop()
        this.playing = false
        this.currentFrame = 0
        this.sliderFrame = 0
        if (this.flow) {
          this.currentNode = null // flow模式下重置当前节点
        }
      }
    },
    reload () {
      if (this.flow) {
        // 流程模式：重新播放流程
        this.playFlow()
      } else if (this.url) {
        // 单动画模式：重新加载动画
        this.player.loadFromUrl(this.url).then(() => {
          this.updateTotalFrames()
          this.play() // 重新加载后自动播放
        })
      }
    },
    playFlow () {
      if (this.player && this.flow) {
        this.currentNode = null // 重新播放前重置当前节点
        this.player.stopFlow() // 重置流程状态
        this.player.playFlow()
        this.playing = true // 设置播放状态
      }
    },
    stopFlow () {
      if (this.player) this.player.stopFlow()
    },
    updateTotalFrames () {
      this.totalFrames = this.player ? this.player.getTotalFrames() : 0
    }
  }
}
</script>

<style scoped>
.animation-canvas-wrapper {
  position: relative;
  display: inline-block;
}
.animation-canvas {
  display: block;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
.controls-overlay {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 18px;
  background: rgba(30, 30, 30, 0.55);
  border-radius: 24px;
  padding: 8px 24px;
  z-index: 15;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  transition: opacity 0.2s;
}
.icon-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  outline: none;
  padding: 6px 10px;
  border-radius: 50%;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.icon-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.12);
}
.progress-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 15;
  pointer-events: none;
}
.frame-info {
  color: #fff;
  font-size: 13px;
  background: rgba(0,0,0,0.35);
  border-radius: 8px;
  padding: 2px 10px;
  margin-right: 0;
  pointer-events: auto;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
